import React, { useEffect, useRef, useState } from 'react';

import { FILLS, SIZING, TYPE } from '../../styles/tokens';
import EmailVerification, { RESEND_CODE_COOLDOWN_SECONDS } from './EmailVerification';
import { useEmailCodeVerification } from './useEmailCodeVerification';

export const LOG_IN_CODE_STEP_DIFFERENT_EMAIL_LABEL = 'Use a different e-mail';

export interface LogInCodeStepProps {
  email: string;
  /** Called once the code is accepted and the confirmation has been shown. */
  onVerified: () => void;
  onUseDifferentEmail: () => void;
  /** Sign-in pop-up only: the primary button renders in the sheet's pinned footer. */
  pinPrimaryToSheetFooter?: boolean;
}

/**
 * Code step for a log-in rejected as unconfirmed: sends one fresh code on open, then
 * verifies it. It never sees the password.
 */
const LogInCodeStep: React.FC<LogInCodeStepProps> = ({
  email,
  onVerified,
  onUseDifferentEmail,
  pinPrimaryToSheetFooter = false,
}) => {
  const {
    verificationError,
    verificationLoading,
    verificationSuccess,
    handleVerifyEmail,
    handleResendCode,
  } = useEmailCodeVerification(email, onVerified);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const hasSentOpeningCode = useRef(false);

  useEffect(() => {
    if (hasSentOpeningCode.current) return;
    hasSentOpeningCode.current = true;
    handleResendCode().then(
      () => setResendCooldownSeconds(RESEND_CODE_COOLDOWN_SECONDS),
      () => {
        // The failure message is already shown by the hook; Resend stays live.
      },
    );
  }, [handleResendCode]);

  return (
    <div style={{ textAlign: 'center' }}>
      <EmailVerification
        email={email}
        onVerifyCode={handleVerifyEmail}
        onResendCode={handleResendCode}
        loading={verificationLoading}
        error={verificationError}
        success={verificationSuccess}
        resendCooldownSeconds={resendCooldownSeconds}
        fillViewport={false}
        pinPrimaryToSheetFooter={pinPrimaryToSheetFooter}
      />
      {!verificationSuccess && (
        <button
          type="button"
          onClick={onUseDifferentEmail}
          style={{
            minHeight: SIZING.touchTarget,
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: FILLS.gold,
            ...TYPE.secondaryStrong,
          }}
        >
          {LOG_IN_CODE_STEP_DIFFERENT_EMAIL_LABEL}
        </button>
      )}
    </div>
  );
};

export default LogInCodeStep;
